import React, {useEffect, useState} from 'react';
import {useNavigate, useLocation} from "react-router-dom";
import api from "../../api";
import {Button, Col, Drawer, Form, Input, message, Row, Switch, Table, Tabs, Upload, Popconfirm} from "antd";
import {UploadOutlined, PlayCircleOutlined, EditOutlined, DeleteOutlined} from '@ant-design/icons';
import dayjs from "dayjs";
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/nl';

dayjs.extend(relativeTime);
dayjs.locale('nl');

export default function AdminSuppliers() {
    const navigate = useNavigate();
    const location = useLocation();
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [form] = Form.useForm();

    // Determine active tab based on route
    const getActiveTab = () => {
        if (location.pathname === '/admin/suppliers') return 'suppliers';
        if (location.pathname === '/admin/icons') return 'icons';
        if (location.pathname === '/admin/users') return 'users';
        if (location.pathname === '/admin/blog') return 'blog';
        if (location.pathname === '/admin/ad-spends') return 'ad-spends';
        if (location.pathname === '/admin/affiliates') return 'affiliates';
        return 'stats';
    };

    const handleTabChange = (key) => {
        switch(key) {
            case 'stats':
                navigate('/admin');
                break;
            case 'suppliers':
                navigate('/admin/suppliers');
                break;
            case 'icons':
                navigate('/admin/icons');
                break;
            case 'users':
                navigate('/admin/users');
                break;
            case 'blog':
                navigate('/admin/blog');
                break;
            case 'ad-spends':
                navigate('/admin/ad-spends');
                break;
            case 'affiliates':
                navigate('/admin/affiliates');
                break;
            default:
                navigate('/admin');
        }
    };

    useEffect(() => {
        loadSuppliers();
    }, []);

    const loadSuppliers = () => {
        setLoading(true);
        api.getSuppliers()
            .then(setSuppliers)
            .finally(() => setLoading(false));
    };

    const handleCreate = () => {
        setEditingSupplier(null);
        form.resetFields();
        setDrawerOpen(true);
    };

    const handleEdit = (supplier) => {
        setEditingSupplier(supplier);
        form.setFieldsValue({
            name: supplier.name,
            website: supplier.website,
            country: supplier.country,
            language: supplier.language,
            logo: supplier.logo,
            is_crawlable: supplier.is_crawlable,
            is_verified: supplier.is_verified,
            crawler: supplier.crawler,
        });
        setDrawerOpen(true);
    };

    const handleDelete = (id) => {
        api.deleteSupplier(id)
            .then(() => {
                message.success('Leverancier verwijderd');
                loadSuppliers();
            })
            .catch(() => message.error('Fout bij verwijderen'));
    };

    const handleSubmit = () => {
        form.validateFields().then((values) => {
            const action = editingSupplier
                ? api.updateSupplier(editingSupplier.id, values)
                : api.createSupplier(values);

            action.then(() => {
                message.success(editingSupplier ? 'Leverancier bijgewerkt' : 'Leverancier aangemaakt');
                setDrawerOpen(false);
                form.resetFields();
                loadSuppliers();
            }).catch(() => message.error('Fout bij opslaan'));
        });
    };

    const handleStartCrawl = (id, name) => {
        api.startSupplierCrawl(id)
            .then((response) => {
                message.success(response.message || `Crawl gestart voor ${name}`);
                loadSuppliers();
            })
            .catch((error) => {
                const errorMsg = error.response?.data?.detail || 'Fout bij starten van crawl';
                message.error(errorMsg);
            });
    };

    const uploadProps = {
        name: 'file',
        beforeUpload: (file) => {
            setUploading(true);
            api.uploadToCdn(file)
                .then((url) => {
                    form.setFieldsValue({ logo: url });
                    message.success('Logo geüpload');
                })
                .catch(() => message.error('Fout bij uploaden'))
                .finally(() => setUploading(false));
            return false; // Prevent default upload behavior
        },
        showUploadList: false,
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: 'Logo',
            dataIndex: 'logo',
            key: 'logo',
            width: 100,
            render: (logo) => logo ? (
                <img src={logo} alt="Logo" style={{maxWidth: 60, maxHeight: 40}} />
            ) : null,
        },
        {
            title: 'Naam',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Website',
            dataIndex: 'website',
            key: 'website',
            render: (website) => website ? (
                <a href={website} target="_blank" rel="noopener noreferrer">{website}</a>
            ) : null,
        },
        {
            title: 'Land',
            dataIndex: 'country',
            key: 'country',
            width: 80,
        },
        {
            title: 'Taal',
            dataIndex: 'language',
            key: 'language',
            width: 80,
        },
        {
            title: 'Producten',
            dataIndex: 'product_offers_count',
            key: 'product_offers_count',
            sorter: (a, b) => a.product_offers_count - b.product_offers_count,
            defaultSortOrder: 'descend',
            render: (count) => count?.toLocaleString('nl-NL') || '0',
        },
        {
            title: 'Crawler',
            dataIndex: 'crawler',
            key: 'crawler',
        },
        {
            title: 'Crawlbaar',
            dataIndex: 'is_crawlable',
            key: 'is_crawlable',
            render: (crawlable) => crawlable ? 'Ja' : 'Nee',
        },
        {
            title: 'Geverifieerd',
            dataIndex: 'is_verified',
            key: 'is_verified',
            render: (verified) => verified ? '✓ Ja' : '✗ Nee',
        },
        {
            title: 'Laatste crawl',
            dataIndex: 'crawl_last_started_at',
            key: 'crawl_last_started_at',
            render: (date) => date ? dayjs(date).fromNow() : 'Nog nooit',
        },
        {
            title: 'Acties',
            key: 'actions',
            width: 200,
            render: (_, supplier) => (
                <Row gutter={8}>
                    <Col>
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(supplier)}
                        >
                            Bewerk
                        </Button>
                    </Col>
                    <Col>
                        <Popconfirm
                            title="Weet je zeker dat je deze leverancier wilt verwijderen?"
                            onConfirm={() => handleDelete(supplier.id)}
                            okText="Ja"
                            cancelText="Nee"
                        >
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            >
                                Verwijder
                            </Button>
                        </Popconfirm>
                    </Col>
                    {supplier.is_crawlable && supplier.crawler && (
                        <Col>
                            <Button
                                size="small"
                                type="primary"
                                icon={<PlayCircleOutlined />}
                                onClick={() => handleStartCrawl(supplier.id, supplier.name)}
                            >
                                Crawl
                            </Button>
                        </Col>
                    )}
                </Row>
            ),
        },
    ];

    return (
        <>
            <Drawer
                title={editingSupplier ? 'Leverancier bewerken' : 'Nieuwe leverancier'}
                width={600}
                open={drawerOpen}
                onClose={() => {
                    setDrawerOpen(false);
                    form.resetFields();
                }}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Naam"
                        rules={[{ required: true, message: 'Naam is verplicht' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="website"
                        label="Website"
                    >
                        <Input placeholder="https://..." />
                    </Form.Item>

                    <Form.Item
                        name="country"
                        label="Land (ISO 3166-1 alpha-2)"
                    >
                        <Input placeholder="NL, BE, DE, etc." maxLength={2} />
                    </Form.Item>

                    <Form.Item
                        name="language"
                        label="Taal (ISO 639-1)"
                    >
                        <Input placeholder="nl, en, de, etc." maxLength={2} />
                    </Form.Item>

                    <Form.Item
                        name="logo"
                        label="Logo URL"
                    >
                        <Input placeholder="https://..." />
                    </Form.Item>

                    <Form.Item label="Upload Logo">
                        <Upload {...uploadProps}>
                            <Button icon={<UploadOutlined />} loading={uploading}>
                                Upload naar CDN
                            </Button>
                        </Upload>
                        {form.getFieldValue('logo') && (
                            <div style={{marginTop: 10}}>
                                <img
                                    src={form.getFieldValue('logo')}
                                    alt="Preview"
                                    style={{maxWidth: 200, maxHeight: 100}}
                                />
                            </div>
                        )}
                    </Form.Item>

                    <Form.Item
                        name="crawler"
                        label="Crawler naam"
                    >
                        <Input placeholder="plukkers, bolster, etc." />
                    </Form.Item>

                    <Form.Item
                        name="is_crawlable"
                        label="Crawlbaar"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        name="is_verified"
                        label="Geverifieerd (alleen geverifieerde leveranciers worden getoond in productenfilters)"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item>
                        <Row gutter={8}>
                            <Col>
                                <Button onClick={() => setDrawerOpen(false)}>
                                    Annuleren
                                </Button>
                            </Col>
                            <Col>
                                <Button type="primary" onClick={handleSubmit}>
                                    {editingSupplier ? 'Bijwerken' : 'Aanmaken'}
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>
                </Form>
            </Drawer>

            <h1>Admin</h1>

            <Tabs
                activeKey={getActiveTab()}
                onChange={handleTabChange}
                items={[
                    {
                        label: 'Stats',
                        key: 'stats',
                        children: null
                    },
                    {
                        label: 'Suppliers',
                        key: 'suppliers',
                        children: (
                            <>
                                <Row style={{marginBottom: 16}}>
                                    <Col span={12}>
                                        <h2>Leveranciers</h2>
                                    </Col>
                                    <Col span={12} style={{textAlign: 'right'}}>
                                        <Button type="primary" onClick={handleCreate}>
                                            Nieuwe leverancier
                                        </Button>
                                    </Col>
                                </Row>

                                <Table
                                    dataSource={suppliers}
                                    columns={columns}
                                    rowKey="id"
                                    loading={loading}
                                    pagination={false}
                                />
                            </>
                        )
                    },
                    {
                        label: 'Icons',
                        key: 'icons',
                        children: null
                    },
                    {
                        label: 'Users',
                        key: 'users',
                        children: null
                    },
                    {
                        label: 'Blog Posts',
                        key: 'blog',
                        children: null
                    },
                    {
                        label: 'Ad Spends',
                        key: 'ad-spends',
                        children: null
                    },
                    {
                        label: 'Affiliates',
                        key: 'affiliates',
                        children: null
                    },
                ]}
            />
        </>
    );
}
